import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    getActivityFAQ,
    deleteActivityFAQ,
    createActivityFAQ,
    updateActivityFAQ
} from "../../../../services/activityService";
import Toster from "../../../common/Toster";
import {
    abAddBtn,
    abDeleteBtn,
    abFieldInput,
    abIndexBadge,
    abItemCard,
    abSectionTitle,
} from "../activityBuilderUi";


const getIncompleteFaqMessage = (faqs) => {
    const incomplete = (faqs || []).find(
        (faq) =>
            String(faq?.question || "").trim() === "" ||
            String(faq?.answer || "").trim() === ""
    );
    if (!incomplete) return null;
    if (String(incomplete.question || "").trim() === "") {
        return "Please enter a question for every FAQ entry.";
    }
    return "Please enter an answer for every FAQ entry.";
};

const ActivityFAQ = ({ activityId, registerSave, onValidityChange }) => {
    const isBuilderMode = Boolean(registerSave);
    const queryClient = useQueryClient();
    const [localFaqs, setLocalFaqs] = useState([{ question: "", answer: "" }]);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Fetch FAQs using React Query
    const { data: faqData, isLoading: isFetching } = useQuery({
        queryKey: ["activityFAQ", activityId],
        queryFn: () => getActivityFAQ(activityId),
        enabled: !!activityId,
    });

    // Delete FAQ mutation
    const deleteFaqMutation = useMutation({
        mutationFn: deleteActivityFAQ,
        onSuccess: () => {
            queryClient.invalidateQueries(["activityFAQ", activityId]);
            setShowSuccess(true);
            setSuccessMessage("FAQ deleted successfully");
        },
        onError: (error) => {
            setShowError(true);
            setError(error.message || "Failed to delete FAQ");
        },
    });

    // Save FAQ mutation
    const saveFaqMutation = useMutation({
        mutationFn: async (faqs) => {
            // Check if we already have FAQs (to decide between create or update)
            // The backend for activity faq seems to expect bulk store according to routes?
            // Actually routes show: Route::post('activity-faqs', 'store');
            // Let's assume store handles bulk or we call it for the whole set.
            return createActivityFAQ({
                activity_id: activityId,
                faqs: faqs
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["activityFAQ", activityId]);
            if (!isBuilderMode) {
                setShowSuccess(true);
                setSuccessMessage("FAQs saved successfully");
            }
        },
        onError: (error) => {
            if (isBuilderMode) return;
            setShowError(true);
            setError(error.message || "Failed to save FAQs");
        }
    });

    // Initialize local FAQs when data is loaded
    useEffect(() => {
        const apiFaqs = Array.isArray(faqData?.data)
            ? faqData.data
            : Array.isArray(faqData?.data?.faqs)
                ? faqData.data.faqs
                : [];

        if (apiFaqs.length) {
            const formattedFaqs = apiFaqs.map((faq) => ({
                id: faq.id,
                question: faq.question,
                answer: faq.answer,
            }));
            setLocalFaqs(formattedFaqs);
        } else if (!isFetching) {
            setLocalFaqs([{ question: "", answer: "" }]);
        }
    }, [faqData, isFetching]);

    const isAllFaqsComplete = (faqs) => {
        const list = faqs || [];
        if (list.length === 0) return false;
        return list.every(
            (faq) =>
                String(faq?.question || "").trim() !== "" &&
                String(faq?.answer || "").trim() !== ""
        );
    };

    useEffect(() => {
        onValidityChange?.(isAllFaqsComplete(localFaqs));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localFaqs]);

    const handleQuestionChange = (index, value) => {
        setLocalFaqs((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], question: value };
            onValidityChange?.(isAllFaqsComplete(next));
            return next;
        });
    };

    const handleAnswerChange = (index, value) => {
        setLocalFaqs((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], answer: value };
            onValidityChange?.(isAllFaqsComplete(next));
            return next;
        });
    };

    const addFAQ = () => {
        setLocalFaqs((prev) => {
            const next = [...prev, { question: "", answer: "" }];
            onValidityChange?.(isAllFaqsComplete(next));
            return next;
        });
    };

    const removeFAQ = async (index) => {
        const faq = localFaqs[index];
        if (faq.id) {
            deleteFaqMutation.mutate(faq.id);
        }
        setLocalFaqs((prev) => {
            const next = prev.filter((_, i) => i !== index);
            const normalized = next.length > 0 ? next : [{ question: "", answer: "" }];
            onValidityChange?.(isAllFaqsComplete(normalized));
            return normalized;
        });
    };

    const handleSave = async () => {
        const validationMessage = getIncompleteFaqMessage(localFaqs);
        if (validationMessage) {
            if (!isBuilderMode) {
                setError(validationMessage);
                setShowError(true);
            }
            throw new Error(validationMessage);
        }

        setIsSubmitting(true);
        try {
            await saveFaqMutation.mutateAsync(localFaqs);
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        registerSave?.(() => handleSave());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [registerSave, localFaqs, activityId]);

    return (
        <div className="relative">
            {showError && error && (
                <Toster message={error} type="error" onClose={() => setShowError(false)} />
            )}
            {showSuccess && successMessage && (
                <Toster message={successMessage} type="success" onClose={() => setShowSuccess(false)} />
            )}

            <div className="space-y-4">
                {localFaqs.map((faq, index) => (
                    <div key={index} className={abItemCard}>
                        <div className="mb-4 flex items-center justify-between">
                            <div className="flex items-center gap-2.5">
                                <span className={abIndexBadge}>{index + 1}</span>
                                <h3 className={abSectionTitle}>FAQ entry</h3>
                            </div>
                            {localFaqs.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => removeFAQ(index)}
                                    className={abDeleteBtn}
                                    title="Remove FAQ"
                                >
                                    <i className="fi fi-rr-trash text-xs"></i>
                                </button>
                            )}
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                                    Question <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={faq.question}
                                    onChange={(e) => handleQuestionChange(index, e.target.value)}
                                    className={abFieldInput}
                                    placeholder="e.g., What should I bring for this activity?"
                                />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                                    Answer <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={faq.answer}
                                    onChange={(e) => handleAnswerChange(index, e.target.value)}
                                    rows={3}
                                    className={`${abFieldInput} min-h-[88px] resize-y`}
                                    placeholder="Provide a detailed answer here..."
                                />
                            </div>
                        </div>
                    </div>
                ))}

                <button type="button" onClick={addFAQ} className={abAddBtn}>
                    <i className="fi fi-rr-plus text-xs"></i>
                    Add FAQ
                </button>
            </div>
        </div>
    );
};

export default ActivityFAQ;
